/**
 * Types and defaults for Audio Filter Configuration.
 */

import type {
  AudioFilterEntryConfig,
  AudioMusicFilterConfig,
  AudioOverlapFilterConfig,
  AudioSilenceFilterConfig,
  AudioSpeakerFilterConfig,
  AudioTimeRangeFilterConfig,
  AudioTurnRangeFilterConfig,
} from "@/types/metrics/metadata"

export type AudioFilterPreset = "full" | "custom"

export type AudioFilterEntryType = AudioFilterEntryConfig["type"]

/** Matches the Zod schema cap on include/exclude list length. */
export const MAX_AUDIO_FILTERS_PER_CATEGORY = 5

/** Backend default — documented so users know what empty means. */
export const SILENCE_MIN_GAP_DEFAULT_SECONDS = 0.5

export const AUDIO_FILTER_TYPES: {
  value: AudioFilterEntryType
  label: string
  description: string
}[] = [
  {
    value: "speaker",
    label: "Speaker",
    description: "Keep only the selected speakers",
  },
  {
    value: "silence",
    label: "Silence",
    description: "Gaps of silence in the audio",
  },
  {
    value: "overlap",
    label: "Overlap",
    description: "Regions where both speakers talk at once",
  },
  {
    value: "music",
    label: "Music",
    description: "Regions detected as music or hold tones",
  },
  {
    value: "time_range",
    label: "Time range",
    description: "First/last/middle N seconds of the call",
  },
  {
    value: "turn_range",
    label: "Turn range",
    description: "First or last N speaker turns",
  },
]

export const AUDIO_SPEAKERS = [
  { value: "agent" as const, label: "Agent" },
  { value: "persona" as const, label: "Persona" },
  { value: "music" as const, label: "Music" },
]

export const AUDIO_TIME_RANGE_MODES = [
  { value: "first_n_seconds" as const, label: "First" },
  { value: "last_n_seconds" as const, label: "Last" },
  { value: "middle_n_seconds" as const, label: "Middle" },
]

export const AUDIO_TURN_RANGE_MODES = [
  { value: "first_n" as const, label: "First" },
  { value: "last_n" as const, label: "Last" },
]

export const AUDIO_TURN_RANGE_SPEAKERS = [
  { value: "any" as const, label: "Any speaker" },
  { value: "agent" as const, label: "Agent" },
  { value: "persona" as const, label: "Persona" },
  { value: "music" as const, label: "Music" },
]

function generateFilterId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function createDefaultSpeakerFilter(): AudioSpeakerFilterConfig {
  return { type: "speaker", speakers: ["agent"], id: generateFilterId() }
}

function createDefaultSilenceFilter(): AudioSilenceFilterConfig {
  return { type: "silence", id: generateFilterId() }
}

function createDefaultOverlapFilter(): AudioOverlapFilterConfig {
  return { type: "overlap", id: generateFilterId() }
}

function createDefaultMusicFilter(): AudioMusicFilterConfig {
  return { type: "music", id: generateFilterId() }
}

function createDefaultTimeRangeFilter(): AudioTimeRangeFilterConfig {
  return {
    type: "time_range",
    mode: "first_n_seconds",
    count: 30,
    id: generateFilterId(),
  }
}

function createDefaultTurnRangeFilter(): AudioTurnRangeFilterConfig {
  return {
    type: "turn_range",
    mode: "last_n",
    count: 3,
    speaker: "any",
    id: generateFilterId(),
  }
}

export function createDefaultAudioFilterEntry(
  type: AudioFilterEntryType,
): AudioFilterEntryConfig {
  switch (type) {
    case "speaker":
      return createDefaultSpeakerFilter()
    case "silence":
      return createDefaultSilenceFilter()
    case "overlap":
      return createDefaultOverlapFilter()
    case "music":
      return createDefaultMusicFilter()
    case "time_range":
      return createDefaultTimeRangeFilter()
    case "turn_range":
      return createDefaultTurnRangeFilter()
  }
}
